import produce from 'immer';
import { UserOrganizationResponse } from '@cdaas/api-clients/dist/asset-user-openapi';
import { ApiError, AuthActions, Credentials, User } from '../../types';
import * as actionTypes from '../actions/actionTypes';

interface PhoneNumberVerification {
  phoneNumber: string | null;
  needsVerification: boolean;
  path: string | null;
}

export interface AuthReducer {
  authenticated: boolean;
  authSequenceReady: boolean;
  loading: boolean;
  error: ApiError;
  autoAuthFailed: boolean;
  credentials: Credentials;
  user: User | null;
  phoneNumberVerification: PhoneNumberVerification;
  requestedPath: string | null;
  pendingApprovals: number | null;
  selectedOrganization: UserOrganizationResponse | null;
  bankIdVerificationNeeded: boolean;
}

export const initialState: AuthReducer = {
  authenticated: false,
  authSequenceReady: false,
  loading: false,
  error: {
    status: false,
    message: null,
  },
  autoAuthFailed: false,
  credentials: {
    accessToken: '',
    expiresIn: 0,
    expiryDate: new Date(0),
  },
  user: null,
  phoneNumberVerification: { phoneNumber: null, needsVerification: false, path: null },
  requestedPath: null,
  pendingApprovals: null,
  selectedOrganization: null,
  bankIdVerificationNeeded: false,
};

const reducer = (state: AuthReducer = initialState, action: AuthActions): AuthReducer => {
  switch (action.type) {
    case actionTypes.AUTH_START:
      return produce(state, draft => {
        draft.authSequenceReady = false;
        draft.loading = true;
        draft.autoAuthFailed = false;
        draft.error = initialState.error;
      });
    case actionTypes.AUTH_SET_CREDENTIALS:
      return produce(state, draft => {
        draft.autoAuthFailed = false;
        draft.credentials = action.credentials;
      });
    case actionTypes.AUTH_SUCCESS:
      return produce(state, draft => {
        draft.authenticated = true;
        draft.authSequenceReady = true;
        draft.loading = false;
        draft.autoAuthFailed = false;
        draft.user = action.user;
        draft.error = initialState.error;
      });
    case actionTypes.AUTH_FAIL:
      return produce(state, draft => {
        draft.authSequenceReady = true;
        draft.loading = false;
        draft.error.status = true;
        draft.error.message = action.error;
      });

    case actionTypes.AUTH_LOGOUT:
      return produce(state, draft => {
        draft.authenticated = initialState.authenticated;
        draft.authSequenceReady = true;
        draft.autoAuthFailed = state.autoAuthFailed;
        draft.credentials = initialState.credentials;
        draft.loading = initialState.loading;
        draft.user = initialState.user;
      });
    case actionTypes.AUTH_ERROR_CLEAR:
      return produce(state, draft => {
        draft.error = initialState.error;
      });
    case actionTypes.AUTH_SET_AUTO_FAIL:
      return produce(state, draft => {
        draft.autoAuthFailed = true;
      });
    case actionTypes.AUTH_SET_USER_INFO:
      return produce(state, draft => {
        draft.user = action.user;
      });
    case actionTypes.AUTH_PHONE_NUMBER_NOT_VERIFIED:
      return produce(state, draft => {
        draft.phoneNumberVerification.needsVerification = true;
        draft.phoneNumberVerification.phoneNumber = action.phoneNumber;
        draft.phoneNumberVerification.path = action.path;
      });
    case actionTypes.AUTH_PHONE_NUMBER_VERIFIED:
      return produce(state, draft => {
        draft.phoneNumberVerification.needsVerification = false;
        draft.phoneNumberVerification.phoneNumber = null;
      });
    case actionTypes.AUTH_SET_REQUESTED_PATH:
      return produce(state, draft => {
        draft.requestedPath = action.path;
      });
    case actionTypes.AUTH_SET_PENDING_APPROVALS:
      return produce(state, draft => {
        draft.pendingApprovals = action.pendingApprovals;
      });
    case actionTypes.AUTH_SET_SELECTED_ORGANIZATION:
      return produce(state, draft => {
        draft.selectedOrganization = action.selectedOrganization;
      });
    case actionTypes.AUTH_BANK_ID_NOT_VERIFIED:
      return produce(state, draft => {
        draft.bankIdVerificationNeeded = true;
      });
    default:
      return state;
  }
};

export default reducer;
